import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import logger from '../utils/logger';

const AuthPage = () => {
  const { login, register, registerCompany, changePassword } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  
  // Password change dialog state
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Candidate registration state
  const [candidateData, setCandidateData] = useState({
    nombre: '',
    email: '',
    password: '',
    telefono: '',
    company_code: '',
    rol: 'candidate',
    departamento: '',
    anos_empresa: 0,
    anos_experiencia_total: 0,
    rango_edad: '',
    nivel_educacion: '',
    ubicacion: '',
    idioma_preferido: language
  });
  
  // Employee registration state
  const [employeeData, setEmployeeData] = useState({
    nombre: '',
    email: '',
    password: '',
    telefono: '',
    rol: 'employee',
    empresa_codigo: '',
    departamento: '',
    anos_empresa: 0,
    anos_experiencia_total: 0,
    rango_edad: '',
    nivel_educacion: '',
    ubicacion: '',
    idioma_preferido: language
  });
  
  // Company registration state
  const [companyData, setCompanyData] = useState({
    nombre: '',
    email_admin: '',
    admin_password: '',
    telefono_admin: '',
    industria: '',
    tamano_empresa: '',
    pais: '',
    idioma_default: language
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      if (result.forcePasswordChange) {
        // Show password change dialog
        setShowPasswordChangeDialog(true);
        toast.info(
          language === 'es' 
            ? 'Debes cambiar tu contraseña antes de continuar' 
            : 'You must change your password before continuing'
        );
      } else {
        toast.success(t('login_success', 'Login successful'));
      }
    } else {
      toast.error(result.error || t('auth.login_failed', 'Login failed'));
    }
    
    setLoading(false);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(
        language === 'es' 
          ? 'La contraseña debe tener al menos 6 caracteres' 
          : 'Password must be at least 6 characters'
      );
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(
        language === 'es' 
          ? 'Las contraseñas no coinciden' 
          : 'Passwords do not match'
      );
      return;
    }
    
    setChangingPassword(true);
    const result = await changePassword(newPassword);
    
    if (result.success) {
      toast.success(
        language === 'es' 
          ? 'Contraseña cambiada exitosamente' 
          : 'Password changed successfully'
      );
      setShowPasswordChangeDialog(false);
      setNewPassword('');
      setConfirmPassword('');
    } else {
      toast.error(result.error || 'Error changing password');
    }
    
    setChangingPassword(false);
  };

  const handleCandidateRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Use the special candidate registration endpoint
      const response = await axios.post('/auth/register-candidate', candidateData);
      
      if (response.data.success) {
        toast.success(t('registration_success', 'Candidate registration successful'));
        // Auto-login after successful registration
        const loginResult = await login(candidateData.email, candidateData.password);
        if (loginResult.success) {
          toast.success(t('auth.login_successful', 'Login successful!'));
        }
      } else {
        toast.error(response.data.message || t('registration_failed', 'Registration failed'));
      }
    } catch (error) {
      logger.error('Candidate registration error:', error);
      logger.error('Error details:', error.response);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message ||
                          t('registration_error', 'Error during registration');
      toast.error(t('auth.registration_failed_error', 'Registration failed: ') + errorMessage);
    }
    
    setLoading(false);
  };

  const handleEmployeeRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await register(employeeData);
    
    if (result.success) {
      toast.success(t('registration_success', 'Registration successful'));
    } else {
      toast.error(result.error || t('auth.login_failed', 'Login failed'));
    }
    
    setLoading(false);
  };

  const handleCompanyRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await registerCompany(companyData);
    
    if (result.success) {
      toast.success(t('company_registration_success', 'Company registered successfully'));
    } else {
      toast.error(result.error || t('auth.login_failed', 'Login failed'));
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl border-t-4" style={{ borderTopColor: '#1A878D' }}>
        <CardHeader className="text-center">
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1"></div>
            <div className="flex-1 flex flex-col items-center min-w-[400px]">
              <img 
                src="/images/mevia-logo-vertical.png" 
                alt="MEVIA Logo" 
                className="h-24 mb-4"
              />
              <CardTitle className="text-2xl font-bold text-gray-900 text-center max-w-md">
                {language === 'es' ? 'Te damos la bienvenida al Sistema de Evaluación MEVIA' : 'Welcome to MEVIA Assessment System'}
              </CardTitle>
            </div>
            <div className="flex-1 flex justify-end">
              <Select 
                value={language} 
                onValueChange={(value) => {
                  changeLanguage(value);
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">🇪🇸 ES</SelectItem>
                  <SelectItem value="en">🇺🇸 EN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="login" className="space-y-6">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="login">{t('login', 'Login')}</TabsTrigger>
              <TabsTrigger value="candidate">{t('candidates', 'Candidates')}</TabsTrigger>
              <TabsTrigger value="employee">{t('employee', 'Employee')}</TabsTrigger>
              <TabsTrigger value="company">{t('company', 'Company')}</TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">{t('email', 'Email')}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder={t('email_placeholder', 'tu@email.com')}
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password">{t('password', 'Contraseña')}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder={t('password_placeholder', 'Tu contraseña')}
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? t('loading', 'Loading...') : t('login', 'Login')}
                </Button>
              </form>
            </TabsContent>
            
            {/* Candidate Registration Tab */}
            <TabsContent value="candidate">
              <form onSubmit={handleCandidateRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('name', 'Full Name')}</Label>
                    <Input
                      placeholder={t('name_placeholder', 'Your full name')}
                      value={candidateData.nombre}
                      onChange={(e) => setCandidateData({...candidateData, nombre: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('email', 'Email')}</Label>
                    <Input
                      type="email"
                      placeholder={t('email_placeholder', 'your@email.com')}
                      value={candidateData.email}
                      onChange={(e) => setCandidateData({...candidateData, email: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('password', 'Password')}</Label>
                    <Input
                      type="password"
                      placeholder={t('password_placeholder', 'Minimum 6 characters')}
                      value={candidateData.password}
                      onChange={(e) => setCandidateData({...candidateData, password: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('phone', 'Phone')}</Label>
                    <Input
                      placeholder={t('phone_placeholder', 'Your phone number')}
                      value={candidateData.telefono}
                      onChange={(e) => setCandidateData({...candidateData, telefono: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('candidate.company_code', 'Company Code')} *</Label>
                    <Input
                      required
                      placeholder={t('candidate.company_code_placeholder', 'Enter company access code')}
                      value={candidateData.company_code || ''}
                      onChange={(e) => setCandidateData({...candidateData, company_code: e.target.value.toUpperCase()})}
                      maxLength={6}
                      className="uppercase"
                    />
                    <p className="text-xs text-gray-500">
                      {t('candidate.company_code_help', 'Ask your target company for their 6-character access code')}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('experience_years', 'Years of experience')}</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={candidateData.anos_experiencia_total}
                      onChange={(e) => setCandidateData({...candidateData, anos_experiencia_total: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('education', 'Education level')}</Label>
                    <Select
                      value={candidateData.nivel_educacion}
                      onValueChange={(value) => setCandidateData({...candidateData, nivel_educacion: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('select_education', 'Select level')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="secundaria">{t('high_school', 'High School')}</SelectItem>
                        <SelectItem value="tecnico">{t('technical', 'Technical')}</SelectItem>
                        <SelectItem value="universitario">{t('university', 'University')}</SelectItem>
                        <SelectItem value="posgrado">{t('postgrad', 'Postgraduate')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="individual-register-button"
                >
                  {loading ? t('loading', 'Loading...') : t('register', 'Register')}
                </Button>
              </form>
            </TabsContent>
            
            {/* Employee Registration Tab */}
            <TabsContent value="employee">
              <form onSubmit={handleEmployeeRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('name', 'Full Name')}</Label>
                    <Input
                      placeholder={t('name_placeholder', 'Your full name')}
                      value={employeeData.nombre}
                      onChange={(e) => setEmployeeData({...employeeData, nombre: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('email', 'Email')}</Label>
                    <Input
                      type="email"
                      placeholder={t('email_placeholder', 'your@email.com')}
                      value={employeeData.email}
                      onChange={(e) => setEmployeeData({...employeeData, email: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('company_code', 'Código de empresa')}</Label>
                    <Input
                      placeholder={t('company_code_placeholder', 'Código de 6 caracteres')}
                      value={employeeData.empresa_codigo}
                      onChange={(e) => setEmployeeData({...employeeData, empresa_codigo: e.target.value.toUpperCase()})}
                      maxLength={6}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('password', 'Password')}</Label>
                    <Input
                      type="password"
                      placeholder={t('password_placeholder', 'Minimum 6 characters')}
                      value={employeeData.password}
                      onChange={(e) => setEmployeeData({...employeeData, password: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('department', 'Department')}</Label>
                    <Input
                      placeholder={t('department_placeholder', 'Tu departamento')}
                      value={employeeData.departamento}
                      onChange={(e) => setEmployeeData({...employeeData, departamento: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('company_years', 'Years in company')}</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={employeeData.anos_empresa}
                      onChange={(e) => setEmployeeData({...employeeData, anos_empresa: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="employee-register-button"
                >
                  {loading ? t('loading', 'Loading...') : t('register', 'Register')}
                </Button>
              </form>
            </TabsContent>
            
            {/* Company Registration Tab */}
            <TabsContent value="company">
              <form onSubmit={handleCompanyRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('company_name', 'Company Name')}</Label>
                    <Input
                      placeholder={t('company_name_placeholder', 'Your company name')}
                      value={companyData.nombre}
                      onChange={(e) => setCompanyData({...companyData, nombre: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('admin_email', 'Administrator Email')}</Label>
                    <Input
                      type="email"
                      placeholder={t('admin_email_placeholder', 'admin@company.com')}
                      value={companyData.email_admin}
                      onChange={(e) => setCompanyData({...companyData, email_admin: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('admin_password', 'Administrator Password')}</Label>
                    <Input
                      type="password"
                      placeholder={t('password_placeholder', 'Mínimo 6 caracteres')}
                      value={companyData.admin_password}
                      onChange={(e) => setCompanyData({...companyData, admin_password: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('admin_phone', 'Administrator Phone')}</Label>
                    <Input
                      placeholder={t('phone_placeholder', 'Número de teléfono')}
                      value={companyData.telefono_admin}
                      onChange={(e) => setCompanyData({...companyData, telefono_admin: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('industry', 'Industry')}</Label>
                    <Input
                      placeholder={t('industry_placeholder', 'Your company sector')}
                      value={companyData.industria}
                      onChange={(e) => setCompanyData({...companyData, industria: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('company_size', 'Tamaño de empresa')}</Label>
                    <Select
                      value={companyData.tamano_empresa}
                      onValueChange={(value) => setCompanyData({...companyData, tamano_empresa: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('select_size', 'Select size')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="startup">{t('company_size_startup', 'Startup (1-10)')}</SelectItem>
                        <SelectItem value="pequena">{t('company_size_small', 'Small (11-50)')}</SelectItem>
                        <SelectItem value="mediana">{t('company_size_medium', 'Medium (51-200)')}</SelectItem>
                        <SelectItem value="grande">{t('company_size_large', 'Large (201-1000)')}</SelectItem>
                        <SelectItem value="enterprise">Enterprise (1000+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="company-register-button"
                >
                  {loading ? t('loading', 'Loading...') : t('register_company', 'Register Company')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideCloseButton={true} onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-teal-600" />
              {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Por seguridad, debes establecer una nueva contraseña antes de continuar.' 
                : 'For security, you must set a new password before continuing.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {language === 'es' ? 'Nueva contraseña' : 'New password'}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Repite la contraseña' : 'Repeat password'}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-500">
                {language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match'}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={handlePasswordChange}
              disabled={changingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              className="w-full"
            >
              {changingPassword 
                ? (language === 'es' ? 'Cambiando...' : 'Changing...') 
                : (language === 'es' ? 'Cambiar Contraseña' : 'Change Password')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;